import React, { useMemo } from 'react'
import { Check, CornerDownRight, Lock, Star } from 'lucide-react'
import { Button, Modal, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { computeDeletePlan, DeletePlanEntry, sampleNamesText } from '../utils/dimensionDeletePlan'
import { tagLabel } from '../utils/dimensionAccess'
import { useDimensionGridActions } from '../hooks/useDimensionGridActions'

const { Text } = Typography

interface DimensionDeleteConfirmProps {
  open: boolean
  flatList: Dimension[]
  dimensionIds: string[]
  canEdit: boolean
  onClose: () => void
}

interface PlanRowProps {
  icon: React.ReactNode
  title: React.ReactNode
  why: string
  count: number
  last?: boolean
}

const PlanRow: React.FC<PlanRowProps> = ({ icon, title, why, count, last }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '20px 1fr auto',
      gap: 12,
      alignItems: 'flex-start',
      padding: '10px 0',
      borderBottom: last ? undefined : '1px solid #f0f0f0'
    }}
  >
    <span style={{ marginTop: 3 }}>{icon}</span>
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Text style={{ fontWeight: 600, fontSize: 14 }}>{title}</Text>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {why}
      </Text>
    </div>
    <Text style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.1 }}>{count}</Text>
  </div>
)

const DimensionDeleteConfirm: React.FC<DimensionDeleteConfirmProps> = ({
  open,
  flatList,
  dimensionIds,
  canEdit,
  onClose
}) => {
  const { t } = useTranslation()
  const { deleteOne, massDelete, isDeleting } = useDimensionGridActions()

  const plan = useMemo(() => computeDeletePlan(flatList, dimensionIds, canEdit), [flatList, dimensionIds, canEdit])
  const blockedByDefault = plan.blocked.filter(b => b.reason === 'default')
  const blockedBySource = plan.blocked.filter(b => b.reason === 'source')
  const blockedByChildren = plan.blocked.filter(b => b.reason === 'children')

  const total = dimensionIds.length
  const deletableCount = plan.deletable.length
  const names = (entries: DeletePlanEntry[]) => entries.map(e => e.dimension.name)

  const handleConfirm = async () => {
    if (plan.deletable.length === 0) return
    if (plan.deletable.length === 1) {
      await deleteOne(plan.deletable[0].dimensionId)
    } else {
      await massDelete(
        flatList,
        plan.deletable.map(d => d.dimensionId)
      )
    }
    onClose()
  }

  const groups: { key: string; row: React.ReactElement }[] = []
  if (deletableCount > 0) {
    groups.push({
      key: 'deletable',
      row: (
        <PlanRow
          icon={<Check size={15} strokeWidth={2} color="#389e0d" />}
          title={t('dimensionsPage:deleteConfirmGroupWillDelete')}
          why={t('dimensionsPage:deleteConfirmWillDeleteWhy')}
          count={deletableCount}
        />
      )
    })
  }
  if (blockedByDefault.length > 0) {
    groups.push({
      key: 'default',
      row: (
        <PlanRow
          icon={<Star size={15} strokeWidth={1.75} />}
          title={
            <>
              {t('dimensionsPage:deleteConfirmGroupDefault')} <Tag color="gold">{t('dimensionsPage:tagDefault')}</Tag>
            </>
          }
          why={
            blockedByDefault.length === 1
              ? t('dimensionsPage:deleteConfirmDefaultWhy', { name: blockedByDefault[0].dimension.name })
              : t('dimensionsPage:deleteConfirmDefaultWhy', { name: sampleNamesText(names(blockedByDefault), t) })
          }
          count={blockedByDefault.length}
        />
      )
    })
  }
  if (blockedBySource.length > 0) {
    groups.push({
      key: 'source',
      row: (
        <PlanRow
          icon={<Lock size={15} strokeWidth={1.75} />}
          title={t('dimensionsPage:deleteConfirmGroupSource')}
          why={`${t('dimensionsPage:deleteConfirmSourceWhy', {
            source: tagLabel(blockedBySource[0].dimension.source)
          })} ${sampleNamesText(names(blockedBySource), t)}`}
          count={blockedBySource.length}
        />
      )
    })
  }
  if (blockedByChildren.length > 0) {
    groups.push({
      key: 'children',
      row: (
        <PlanRow
          icon={<CornerDownRight size={15} strokeWidth={1.75} />}
          title={t('dimensionsPage:deleteConfirmGroupChildren')}
          why={`${t('dimensionsPage:deleteConfirmChildrenWhy')} ${sampleNamesText(names(blockedByChildren), t)}`}
          count={blockedByChildren.length}
        />
      )
    })
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('global:no')}
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={isDeleting}
          disabled={deletableCount === 0}
          onClick={handleConfirm}
          style={{ marginLeft: 8 }}
        >
          {t('dimensionsPage:actionDeleteN', { count: deletableCount })}
        </Button>
      ]}
    >
      <div style={{ marginBottom: 4 }}>
        <Typography.Title level={5} style={{ margin: '0 0 4px' }}>
          {plan.blocked.length === 0
            ? t('dimensionsPage:deleteConfirmHeadingAll', { total })
            : t('dimensionsPage:deleteConfirmHeadingPartial', { deletable: deletableCount, total })}
        </Typography.Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {plan.blocked.length === 0
            ? t('dimensionsPage:deleteConfirmSubAll')
            : t('dimensionsPage:deleteConfirmSubPartial', { count: plan.blocked.length })}
        </Text>
      </div>
      <div style={{ marginTop: 8 }}>
        {groups.map((g, i) => (
          <React.Fragment key={g.key}>{React.cloneElement(g.row, { last: i === groups.length - 1 })}</React.Fragment>
        ))}
      </div>
    </Modal>
  )
}

export default DimensionDeleteConfirm
